import { FormatHelpers } from '../../helpers/format-helpers';
import { ObjectHelpers } from '../../helpers/object-helpers';
import { ChartStyleColorModel } from '../chart-style/chart-style-color.model';
import { TemporaryStopLossModel } from '../stop-loss/temporary-stop-loss.model';

interface OrderProfitAnnotationResult {
    text: string;
    showTop: boolean;
}

export class TradeOrderDataModel {
    public id = '';
    public accountName = '';
    public price = 0;
    public isTemporary = false;
    public entryPrice = 0;
    public entryTime: Date | null = null;
    public shares = 0;
    public positionInfoShares = 0;
    public allowMoving = false;
    public isLong = false;
    public isEntryOrder: boolean | null = null;
    public color = new ChartStyleColorModel();

    public tradeType = '';
    public ocaGroup = '';
    public statusText = '';
    public stopPrice = 0;
    public limitPrice = 0;
    public currentShares = 0;

    public goodAfterTime: Date | null = null;
    public created: Date | null = null;
    public updated: Date | null = null;
    
    public temporaryStopLoss: TemporaryStopLossModel | null = null;

    constructor(drawTradeData?: any) {
        if (drawTradeData) {
            ObjectHelpers.setProperties(drawTradeData, this, true);

            this.color = new ChartStyleColorModel(drawTradeData.color);
            if (this.isLong) {
                this.color = new ChartStyleColorModel({
                    r: 0,
                    g: 79,
                    b: 208
                });
            } else {
                this.color = new ChartStyleColorModel({
                    r: 190,
                    g: 30,
                    b: 45
                });
            }
        }
    }

    public getCancelOrderMessage(): string {
        let orderType = '';

        if (this.stopPrice > 0) {
            if (this.limitPrice > 0) {
                orderType = 'Stop Limit';
            } else {
                orderType = 'Stop';
            }
        } else if (this.limitPrice > 0) {
            orderType = 'Limit';
        } else {
            orderType = 'Market';
        }

        const buysell = this.isLong ? 'Buy' : 'Sell';

        let description = 'Cancel ' + orderType + ' order to ' + buysell + ' ' + this.shares + ' shrs';
        if (this.stopPrice > 0)
            description += ' at stop ' + FormatHelpers.numberToStringThousandSeparators(this.stopPrice, true);
        if (this.limitPrice > 0)
            description += ' w/limit ' + FormatHelpers.numberToStringThousandSeparators(this.limitPrice, true);

        description += ' ?';

        return description;
    }

    public getOrderDescriptionTooltip(): string {
        let orderType = '';

        if (this.stopPrice > 0) {
            if (this.limitPrice > 0) {
                orderType = 'Stop Limit';
            } else {
                orderType = 'Stop';
            }
        } else if (this.limitPrice > 0) {
            orderType = 'Limit';
        } else {
            orderType = 'Market';
        }

        const buysell = this.isLong ? 'Buy' : 'Sell';

        let description = orderType + ' order to ' + buysell + ' ' + this.shares + ' shrs';
        if (this.stopPrice > 0)
            description += ' at stop ' + FormatHelpers.numberToStringThousandSeparators(this.stopPrice, true);
        if (this.limitPrice > 0)
            description += ' w/limit ' + FormatHelpers.numberToStringThousandSeparators(this.limitPrice, true);

        description += ' (' + this.statusText + ', orderid: ' + this.id + ')';

        description += '&#013;Account: ' + this.accountName;
        return description;
    }

    public getBriefOrderDescription(changedTo: number | null): string {
        let openingClosing = '';
        if (this.isEntryOrder != null && this.isEntryOrder == false && Math.abs(this.currentShares) == this.shares)
            openingClosing = 'CLOSING ORDER ';
        else if (this.isEntryOrder != null && this.isEntryOrder == false && Math.abs(this.currentShares) != this.shares)
            openingClosing = 'REDUCING ORDER ';
        else if (this.currentShares == 0)
            openingClosing = 'OPENING ORDER ';

        if (this.isLong) {
            openingClosing += 'BUY ';
        }
        else {
            openingClosing += 'SELL ';
        }

        let description = '';
        if (this.stopPrice > 0) {
            if (this.limitPrice > 0) 
                description = openingClosing + 'STOP @ ' + FormatHelpers.numberToStringThousandSeparators(this.stopPrice, true) + ' LIMIT @ ' + FormatHelpers.numberToStringThousandSeparators(this.limitPrice, true);
            else if (changedTo != null)
                description = openingClosing + 'STOP @ ' + FormatHelpers.numberToStringThousandSeparators(changedTo, true);
            else
                description = openingClosing + 'STOP @ ' + FormatHelpers.numberToStringThousandSeparators(this.stopPrice, true);
        }
        else if (this.limitPrice > 0) {
            if (changedTo != null)
                description = openingClosing + 'LIMIT @ ' + FormatHelpers.numberToStringThousandSeparators(changedTo, true);
            else
                description = openingClosing + 'LIMIT @ ' + FormatHelpers.numberToStringThousandSeparators(this.limitPrice, true);
        }
        return description;
    }

    public getOrderProfitAnnotation(isDarkTheme: boolean): OrderProfitAnnotationResult {
        const profitHere = (this.price - this.entryPrice) * this.positionInfoShares;
        const isExitOrder = (this.positionInfoShares > 0 && !this.isLong) || (this.positionInfoShares < 0 && this.isLong);
        let profitHereDisplay = '$' + FormatHelpers.numberToStringThousandSeparators(Math.abs(profitHere), true);;
        let color = '';
        let showTop = false;
        if (profitHere > 0 && isExitOrder) {
            profitHereDisplay = "+" + profitHereDisplay;
            showTop = true;
            if (this.statusText?.toLowerCase() == "filled")
                color = isDarkTheme ? 'rgb(0, 255, 0)' : 'rgb(185, 215, 185)';
            else
                color = 'green';
        } else if (profitHere < 0 && isExitOrder) {
            profitHereDisplay = "-" + profitHereDisplay;
            if (this.statusText?.toLowerCase() == "filled")
                color = isDarkTheme ? 'rgb(255, 0, 0)' : 'rgb(255, 178, 178)';
            else
                color = 'red';
        } else {
            color = 'black';
        }

        const htmlToReturn = '<b style="margin-top: 0;margin-bottom: 0;margin-left: 6px; display: inline-block; color:' + color + ';">' + profitHereDisplay + '</b>';
        return {
            text: isExitOrder ? htmlToReturn : '',
            showTop: showTop
        };
    }

    public getClassName(): string {
        return 'trade-plot-line-' + this.id.split(' ').join('').replace(/[^\w\s]/gi, '').toLowerCase();
    }
}